import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { api } from '../../../services/api'
import {
  createDefaultConfig,
  getTerminalColumnCount,
  normalizeLoadedItem,
  reconcileBoard,
  reconcileConfig,
} from './conductorConfig'

const DEFAULT_SYSTEM = '127V/220V'

function createBoard(id, name, parentId) {
  return { id, name, parentId, ...createDefaultConfig(), neutralLoaded: false, idrs: [], circuits: [] }
}

function renumber(circuits) {
  return circuits.map((circuit, index) => ({ ...circuit, id: index + 1 }))
}

// Estado da calculadora: quadros, circuitos, IDRs, regras de demanda e resultado. Toda edição marca o resultado
// como desatualizado (isStale) sem apagá-lo.
export function useBoardsState() {
  const [selectedSystem, setSelectedSystem] = useState(DEFAULT_SYSTEM)
  const [supplyType, setSupplyType] = useState('three-phase')
  const [temperatureFactor, setTemperatureFactor] = useState(1)
  const [boards, setBoards] = useState(() => [createBoard('QD1', 'Quadro geral', null)])
  const [activeBoardId, setActiveBoardId] = useState('QD1')
  const [conductorOptions, setConductorOptions] = useState(null)
  const [groupingOptions, setGroupingOptions] = useState(null)
  const [demandRules, setDemandRules] = useState([])
  const [isLoadingRules, setIsLoadingRules] = useState(true)
  const [result, setResult] = useState('')
  const [isStale, setIsStale] = useState(false)

  const allowsTwoPoles = supplyType !== 'single-phase'
  const activeBoard = boards.find((board) => board.id === activeBoardId) || boards[0]
  const circuits = activeBoard.circuits
  const activeIdrs = activeBoard.idrs || []

  // Toda alteração dos quadros passa por aqui para marcar o resultado como desatualizado.
  const updateBoards = useCallback((updater) => {
    setBoards(updater)
    setIsStale(true)
  }, [])

  const updateActiveBoard = useCallback(
    (transform) => {
      updateBoards((prevBoards) => prevBoards.map((board) => (board.id === activeBoard.id ? transform(board) : board)))
    },
    [activeBoard.id, updateBoards]
  )

  const updateActiveCircuits = useCallback(
    (transform) => updateActiveBoard((board) => ({ ...board, circuits: transform(board.circuits) })),
    [updateActiveBoard]
  )

  const fetchDefaultRules = useCallback(async () => {
    setIsLoadingRules(true)

    try {
      const response = await api.get('/electrical/circuits/data')
      setConductorOptions(response.data.conductorOptions || null)
      setGroupingOptions(response.data.groupingOptions || null)
      return response.data.demandRules || []
    } catch (error) {
      console.error('Erro ao carregar regras de demanda:', error)
      toast.error('Não foi possível carregar as regras de demanda padrão.')
      return null
    } finally {
      setIsLoadingRules(false)
    }
  }, [])

  useEffect(() => {
    let active = true

    fetchDefaultRules().then((rules) => {
      if (active && rules) setDemandRules(rules)
    })

    return () => {
      active = false
    }
  }, [fetchDefaultRules])

  // ---- Dados gerais ----

  function changeSystem(value) {
    setSelectedSystem(value)
    setIsStale(true)
  }

  function changeTemperatureFactor(value) {
    setTemperatureFactor(value)
    setIsStale(true)
  }

  function changeSupplyType(value) {
    setSupplyType(value)
    // A alimentação muda a coluna de capacidade e a condição de neutro carregado dos alimentadores.
    updateBoards((prevBoards) =>
      prevBoards.map((board) => {
        const reconciled = reconcileBoard(board, value, conductorOptions, groupingOptions)

        return value === 'single-phase'
          ? { ...reconciled, circuits: board.circuits.map((circuit) => ({ ...circuit, isTwoPoles: false })) }
          : reconciled
      })
    )
  }

  // ---- Regras de demanda ----

  function clearRuleReferences(isRemoved) {
    updateBoards((prevBoards) =>
      prevBoards.map((board) => ({
        ...board,
        circuits: board.circuits.map((circuit) =>
          circuit.demandRuleId && isRemoved(circuit.demandRuleId) ? { ...circuit, demandRuleId: undefined } : circuit
        ),
      }))
    )
  }

  function saveRule(rule, isNew) {
    setDemandRules((prevRules) =>
      isNew ? [...prevRules, rule] : prevRules.map((item) => (item.id === rule.id ? rule : item))
    )
    setIsStale(true)
    toast.success(isNew ? 'Regra de demanda adicionada' : 'Regra de demanda atualizada')
  }

  function deleteRule(ruleId) {
    setDemandRules((prevRules) => prevRules.filter((rule) => rule.id !== ruleId))
    clearRuleReferences((id) => id === ruleId)
    toast.success('Regra de demanda removida')
  }

  async function restoreDefaultRules() {
    const rules = await fetchDefaultRules()

    if (!rules) return

    setDemandRules(rules)
    clearRuleReferences((id) => !rules.some((rule) => rule.id === id))
    toast.success('Regras padrão restauradas')
  }

  // ---- Quadros ----

  function selectBoard(boardId) {
    setActiveBoardId(boardId)
  }

  function addBoard({ name, parentId }) {
    let number = boards.length + 1
    while (boards.some((board) => board.id === `QD${number}`)) number++
    const id = `QD${number}`

    updateBoards((prevBoards) => [
      ...prevBoards,
      createBoard(id, name, prevBoards.some((board) => board.id === parentId) ? parentId : activeBoard.id),
    ])
    setActiveBoardId(id)
    toast.success('Quadro adicionado')
  }

  function removeBoard(boardId) {
    const board = boards.find((item) => item.id === boardId)

    if (!board || board.parentId === null) {
      toast.error('O quadro raiz não pode ser removido')
      return
    }

    if (boards.some((item) => item.parentId === boardId)) {
      toast.error('Remova ou realoque primeiro os quadros alimentados por este quadro')
      return
    }

    updateBoards((prevBoards) => prevBoards.filter((item) => item.id !== boardId))
    if (activeBoard.id === boardId) setActiveBoardId(board.parentId)
    toast.success('Quadro removido')
  }

  // Substitui a configuração de condutor/agrupamento do alimentador de um quadro.
  function changeBoardConfig(boardId, config) {
    updateBoards((prevBoards) => prevBoards.map((board) => (board.id === boardId ? { ...board, ...config } : board)))
  }

  // ---- Circuitos (do quadro ativo) ----

  // index === null cria um circuito novo; caso contrário, atualiza o circuito da posição indicada.
  function saveCircuit(index, data) {
    updateActiveCircuits((prevCircuits) => {
      if (index === null) {
        return [...prevCircuits, { ...data, id: prevCircuits.length + 1 }]
      }

      return prevCircuits.map((circuit, circuitIndex) =>
        circuitIndex === index ? { ...circuit, ...data, id: circuit.id } : circuit
      )
    })
    toast.success(index === null ? 'Circuito adicionado' : 'Circuito atualizado')
  }

  function duplicateCircuit(index) {
    updateActiveCircuits((prevCircuits) => {
      const source = prevCircuits[index]
      // A cópia não herda o IDR: o vínculo é uma decisão separada.
      const copy = {
        ...source,
        idrId: undefined,
        name: `${source.name} (cópia)`,
        elements: (source.elements || []).map((element) => ({ ...element })),
      }

      return renumber([...prevCircuits.slice(0, index + 1), copy, ...prevCircuits.slice(index + 1)])
    })
    toast.success('Circuito duplicado')
  }

  function removeCircuit(index) {
    updateActiveCircuits((prevCircuits) => renumber(prevCircuits.filter((_, circuitIndex) => circuitIndex !== index)))
    toast.success('Circuito removido')
  }

  function moveCircuit(from, to) {
    updateActiveCircuits((prevCircuits) => {
      if (to < 0 || to >= prevCircuits.length || from === to) return prevCircuits

      const next = [...prevCircuits]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)

      return renumber(next)
    })
  }

  // ---- IDRs (do quadro ativo) ----

  // idrId === null cria um IDR novo. circuitIds são os ids (números) dos circuitos protegidos; marcar um circuito que
  // já estava em outro IDR o move para este.
  function saveIdr(idrId, { name, circuitIds }) {
    const selected = new Set(circuitIds)

    updateActiveBoard((board) => {
      const idrs = board.idrs || []
      let id = idrId
      let nextIdrs

      if (id) {
        nextIdrs = idrs.map((idr) => (idr.id === id ? { ...idr, name } : idr))
      } else {
        let number = idrs.length + 1
        while (idrs.some((idr) => idr.id === `DR${number}`)) number++
        id = `DR${number}`
        nextIdrs = [...idrs, { id, name }]
      }

      return {
        ...board,
        idrs: nextIdrs,
        circuits: board.circuits.map((circuit) => {
          if (selected.has(circuit.id)) return { ...circuit, idrId: id }

          return circuit.idrId === id ? { ...circuit, idrId: undefined } : circuit
        }),
      }
    })
    toast.success(idrId ? 'IDR atualizado' : 'IDR adicionado')
  }

  function removeIdr(idrId) {
    updateActiveBoard((board) => ({
      ...board,
      idrs: (board.idrs || []).filter((idr) => idr.id !== idrId),
      circuits: board.circuits.map((circuit) => (circuit.idrId === idrId ? { ...circuit, idrId: undefined } : circuit)),
    }))
    toast.success('IDR removido')
  }

  // ---- Salvar / carregar JSON ----

  function saveToFile() {
    const stateData = {
      selectedSystem,
      supplyType,
      temperatureFactor,
      boards,
      activeBoardId: activeBoard.id,
      demandRules,
      result,
    }
    const blob = new Blob([JSON.stringify(stateData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Circuitos Elétricos.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function loadFromFile(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        const loadedSupplyType = jsonData.supplyType || 'three-phase'
        // Arquivos antigos (sem quadros) guardavam apenas a lista de circuitos.
        const loadedBoards =
          Array.isArray(jsonData.boards) && jsonData.boards.length > 0
            ? jsonData.boards
            : [{ id: 'QD1', name: 'Quadro geral', parentId: null, circuits: jsonData.circuits || [] }]
        // Arquivos antigos não tinham condutor/agrupamento: completa com os padrões (B1, PVC, caso 1, um circuito).
        let legacyFactorDropped = false
        // A contagem de condutores carregados passou a ser automática: descarta o valor antigo e ajusta métodos e
        // disposições à coluna usada em cada circuito e alimentador.
        const completeBoards = loadedBoards.map((board) => {
          const normalizedBoard = normalizeLoadedItem(board)
          legacyFactorDropped ||= normalizedBoard.legacyFactorDropped
          // Arquivos sem IDRs (ou com vínculos para IDRs inexistentes) continuam válidos.
          const idrs = Array.isArray(board.idrs) ? board.idrs : []

          return {
            ...reconcileBoard(normalizedBoard.item, loadedSupplyType, conductorOptions, groupingOptions),
            idrs,
            circuits: (board.circuits || []).map((circuit) => {
              const normalizedCircuit = normalizeLoadedItem(circuit)
              legacyFactorDropped ||= normalizedCircuit.legacyFactorDropped
              const reconciled = reconcileConfig(
                normalizedCircuit.item,
                conductorOptions,
                groupingOptions,
                getTerminalColumnCount(conductorOptions)
              )

              return idrs.some((idr) => idr.id === reconciled.idrId) ? reconciled : { ...reconciled, idrId: undefined }
            }),
          }
        })

        setSelectedSystem(jsonData.selectedSystem || DEFAULT_SYSTEM)
        setSupplyType(loadedSupplyType)
        setTemperatureFactor(jsonData.temperatureFactor ?? 1)
        setBoards(completeBoards)
        setActiveBoardId(
          loadedBoards.some((board) => board.id === jsonData.activeBoardId)
            ? jsonData.activeBoardId
            : loadedBoards[0].id
        )
        if (jsonData.demandRules) setDemandRules(jsonData.demandRules)
        setResult(jsonData.result || '')
        setIsStale(false)

        if (legacyFactorDropped) {
          toast.info(
            'Fatores de agrupamento manuais antigos foram substituídos pelo padrão (B1, caso 1, um circuito). Revise os condutores.'
          )
        }
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  return {
    selectedSystem,
    supplyType,
    temperatureFactor,
    boards,
    activeBoard,
    circuits,
    activeIdrs,
    allowsTwoPoles,
    conductorOptions,
    groupingOptions,
    demandRules,
    isLoadingRules,
    result,
    isStale,
    setResult,
    setIsStale,
    changeSystem,
    changeTemperatureFactor,
    changeSupplyType,
    saveRule,
    deleteRule,
    restoreDefaultRules,
    selectBoard,
    addBoard,
    removeBoard,
    changeBoardConfig,
    saveCircuit,
    duplicateCircuit,
    removeCircuit,
    moveCircuit,
    saveIdr,
    removeIdr,
    saveToFile,
    loadFromFile,
  }
}
